'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getWebsiteTextContent } from '@/src/lib/sanity.queries';
import mapImage from '@/public/mapImage.png';
import arrow from '@/public/arrow.svg';
import messageIcon from '@/public/messageIcon.svg';
import callIconOutline from '@/public/callIconOutline.svg';
import addressIcon from '@/public/addressIcon.svg';

type ContactFormData = {
  name: string;
  number: string;
  email: string;
  subject: string;
  message: string;
};

export default function ContactUsSection({ locale }: { locale: string }) {
  const t = useTranslations('Home.ContactUsSection');
  const isRTL = locale === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  type Content = {
    contactUsSectionSmallTitle?: string;
    contactUsSectionTitle?: string;
    contactUsSectionDesc?: string;
    contactUsSectionNumber?: string;
    contactUsSectionEmail?: string;
    contactUsSectionAddress?: string;
    contactUsSectionFormTitle?: string;
    contactUsSectionFormDesc?: string;
  };

  const [content, setContent] = useState<Content | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');

  // Create validation schema with translations
  const contactFormSchema = yup.object({
    name: yup
      .string()
      .required(t('ValidationMessages.NameRequired'))
      .min(2, t('ValidationMessages.NameMinLength'))
      .max(50, t('ValidationMessages.NameMaxLength')),
    number: yup
      .string()
      .required(t('ValidationMessages.PhoneRequired'))
      .matches(/^[+]?[\d\s\-()]+$/, t('ValidationMessages.PhoneInvalid'))
      .min(8, t('ValidationMessages.PhoneMinLength')),
    email: yup
      .string()
      .required(t('ValidationMessages.EmailRequired'))
      .email(t('ValidationMessages.EmailInvalid'))
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('ValidationMessages.EmailFormat'))
      .test('real-email', t('ValidationMessages.EmailReal'), (value) => {
        if (!value) return false;
        const invalidDomains = ['test.com', 'example.com', 'invalid.com', 'test.test'];
        const domain = value.split('@')[1];
        return !invalidDomains.includes(domain?.toLowerCase());
      }),
    subject: yup
      .string()
      .required(t('ValidationMessages.SubjectRequired'))
      .min(3, t('ValidationMessages.SubjectMinLength'))
      .max(100, t('ValidationMessages.SubjectMaxLength')),
    message: yup
      .string()
      .required(t('ValidationMessages.MessageRequired'))
      .min(10, t('ValidationMessages.MessageMinLength'))
      .max(1000, t('ValidationMessages.MessageMaxLength')),
  });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: yupResolver(contactFormSchema),
    mode: 'onChange', // Validate on change for real-time feedback
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await getWebsiteTextContent(locale);
        setContent(data || {});
      } catch (e) {
        console.error('Failed to fetch ContactUs content:', e);
      }
    })();
  }, [locale]);

  /* ---------- handlers ---------- */

  const onSubmit = async (data: ContactFormData) => {
    setStatus('sending');
    try {
      // eslint-disable-next-line no-undef
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      setStatus('ok');
      reset(); // Reset form after successful submission
    } catch (err) {
      console.error('[ContactUsSection] submit failed:', err);
      setStatus('error');
    } finally {
      // eslint-disable-next-line no-undef
      setTimeout(() => setStatus('idle'), 60000);
    }
  };

  const isDisabled = isSubmitting || status === 'sending';

  return (
    <section
      id="ContactUsSection"
      dir={dir}
      className={`relative bg-[#561952] px-5 py-20 lg:px-10`}>
      <div className="mx-auto flex max-w-[1440px] flex-col gap-10 md:gap-15 lg:flex-row lg:items-start">
        {/* Background */}
        <Image
          src={mapImage}
          alt="Office Location"
          className="absolute inset-0 z-1 h-full w-full object-cover opacity-4"
        />

        {/* Left content */}
        <div className="z-10 flex w-full flex-1 flex-col items-end justify-end">
          <h2 className="text-secondary-500 w-full text-3xl leading-tight font-bold lg:text-6xl">
            {content?.contactUsSectionTitle ?? t('Title')}
          </h2>
          <h2 className="w-full text-lg leading-tight text-white lg:text-2xl">
            {content?.contactUsSectionDesc ?? t('Desc')}
          </h2>

          {/* ↓↓↓ reduced top-margin on large screens ↓↓↓ */}
          <div className="mt-8 flex w-full flex-row flex-wrap items-start justify-start gap-5 text-white md:gap-20 lg:mt-36">
            <div className="flex flex-col items-start justify-start gap-3">
              <Image
                height={10}
                src={messageIcon}
                alt="callIconOutline"
                className="h-5 w-auto flex-1 sm:h-7 sm:w-auto"
              />
              <span className="text-sm">{t('LiveChat')}</span>
              <p className="text-xs">{content?.contactUsSectionEmail ?? 'info@erammedia.com'}</p>
            </div>
            <div className="flex flex-col items-start justify-start gap-3">
              <Image
                height={10}
                src={callIconOutline}
                alt="messageIcon"
                className="h-5 w-auto flex-1 sm:h-7 sm:w-auto"
              />
              <span className="text-sm">{t('CallUs')}</span>
              <p dir="ltr" className="text-xs">
                {content?.contactUsSectionNumber ?? '+966500578040'}
              </p>
            </div>
            <div className="flex flex-col items-start justify-start gap-3">
              <Image
                height={10}
                src={addressIcon}
                alt="addressIcon"
                className="h-5 w-auto flex-1 sm:h-7 sm:w-auto"
              />
              <span className="text-sm">{t('Address')}</span>
              <p className="max-w-[220px] text-xs">
                {content?.contactUsSectionAddress ?? t('AddressKSA')}
              </p>
            </div>
          </div>

          <Image
            src={mapImage}
            alt="Office Location"
            className="mt-10 h-auto w-full cursor-pointer rounded-[50px] md:mt-20"
            onClick={() => {
              window.open('https://maps.google.com/?q=24.763924,46.692471', '_blank');
            }}
          />
        </div>

        {/* Form section */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="z-10 flex max-h-full w-full flex-1 flex-col items-start justify-center gap-8 rounded-3xl bg-white p-5 px-4 font-medium text-black md:p-8 lg:rounded-[50px] lg:p-12">
          <h3 className="text-2xl font-normal lg:text-6xl">
            {content?.contactUsSectionFormTitle ?? t('FormTitle')}
          </h3>
          <span className="text-xl font-normal whitespace-pre-line text-[#5A5A5A] lg:text-2xl">
            {content?.contactUsSectionFormDesc ?? t('FormDesc')}
          </span>

          {/* name */}
          <div className="w-full">
            <input
              {...register('name')}
              className={`w-full border-b p-2 text-sm font-normal transition-colors outline-none sm:text-base ${
                errors.name
                  ? 'border-red-500 focus:border-red-600'
                  : 'focus:border-primary-400 border-gray-300'
              }`}
              placeholder={t('PlaceholderName')}
              disabled={isDisabled}
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
          </div>

          {/* number */}
          <div className="w-full">
            <input
              {...register('number')}
              className={`w-full border-b p-2 text-sm font-normal transition-colors outline-none sm:text-base ${
                errors.number
                  ? 'border-red-500 focus:border-red-600'
                  : 'focus:border-primary-400 border-gray-300'
              }`}
              placeholder={t('PlaceholderNumber')}
              disabled={isDisabled}
            />
            {errors.number && <p className="mt-1 text-xs text-red-500">{errors.number.message}</p>}
          </div>

          {/* email */}
          <div className="w-full">
            <input
              {...register('email')}
              type="email"
              className={`w-full border-b p-2 text-sm font-normal transition-colors outline-none sm:text-base ${
                errors.email
                  ? 'border-red-500 focus:border-red-600'
                  : 'focus:border-primary-400 border-gray-300'
              }`}
              placeholder={t('PlaceholderEmail')}
              disabled={isDisabled}
            />
            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
          </div>

          {/* subject */}
          <div className="w-full">
            <input
              {...register('subject')}
              className={`w-full border-b p-2 text-sm font-normal transition-colors outline-none sm:text-base ${
                errors.subject
                  ? 'border-red-500 focus:border-red-600'
                  : 'focus:border-primary-400 border-gray-300'
              }`}
              placeholder={t('PlaceholderSubject')}
              disabled={isDisabled}
            />
            {errors.subject && (
              <p className="mt-1 text-xs text-red-500">{errors.subject.message}</p>
            )}
          </div>

          {/* message (textarea for multi-line) */}
          <div className="w-full">
            <textarea
              {...register('message')}
              className={`h-32 w-full resize-none border-b p-2 text-sm font-normal transition-colors outline-none sm:text-base ${
                errors.message
                  ? 'border-red-500 focus:border-red-600'
                  : 'focus:border-primary-400 border-gray-300'
              }`}
              placeholder={t('PlaceholderMessage')}
              disabled={isDisabled}
            />
            {errors.message && (
              <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>
            )}
          </div>

          {/* status feedback */}
          {status === 'ok' && <p className="text-green-600">{t('SuccessMessage')}</p>}
          {status === 'error' && <p className="text-red-600">{t('ErrorMessage')}</p>}

          {/* submit button */}
          <button
            type="submit"
            disabled={isDisabled}
            className={`flex items-center justify-center gap-3 rounded-4xl px-4 py-3 transition-colors md:px-7 md:py-5 ${
              isDisabled
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-primary-400 hover:bg-primary-600 cursor-pointer text-white'
            }`}>
            <p>{isDisabled ? t('Sending') : t('SubmitButton')}</p>
            <Image
              height={10}
              src={arrow}
              alt="arrow"
              className={`h-5 w-auto flex-1 sm:h-7 sm:w-auto ${isRTL ? 'rotate-180' : ''}`}
            />
          </button>
        </form>
      </div>
    </section>
  );
}
